import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      // Always show success (avoid enumeration) — the API also always returns 200.
      setSent(true);
    } catch (err) {
      // Even on rate-limit / server error we show a soft prompt.
      toast.error(err?.response?.data?.detail || "Something went wrong — try again in a minute.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 dark bg-background text-foreground">
      <Toaster theme="dark" richColors />
      <div className="hidden md:block relative overflow-hidden bg-black">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 20% 20%, rgba(37,99,235,0.25), transparent 45%), radial-gradient(circle at 80% 30%, rgba(168,85,247,0.22), transparent 50%), radial-gradient(circle at 60% 80%, rgba(249,115,22,0.18), transparent 50%)" }} />
        <div className="relative z-10 p-10 lg:p-14 h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/zenith360-logo.png" alt="Zenith 360" className="h-10 w-auto" />
            <div>
              <div className="font-heading font-bold text-lg text-logo-gradient">Zenith 360 ERP</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/60">India · AI-native</div>
            </div>
          </Link>
          <div>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-white">
              Forgot your<br /><span className="text-logo-gradient">password?</span>
            </h1>
            <div className="text-sm text-white/60 mt-4 max-w-xs">We'll email you a one-time link. Valid for 60 minutes.</div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Emails delivered via Resend · TLS · SPF/DKIM</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10 bg-background">
        {sent ? (
          <div className="w-full max-w-sm space-y-5" data-testid="fp-sent">
            <div className="w-12 h-12 rounded-sm bg-emerald-500/15 text-emerald-500 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h2 className="font-heading text-2xl font-bold">Check your inbox</h2>
            <p className="text-sm text-foreground/70">
              If an account exists for <b>{email}</b>, we've sent a password-reset link. It expires
              in <b>60 minutes</b>. Not seeing it? Check spam or wait a minute.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="forgot-form">
            <div className="space-y-1.5">
              <div className="text-[11px] font-mono uppercase tracking-widest text-accent">// Password recovery</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight">Reset password</h2>
              <div className="text-sm text-foreground/70">
                Enter the email you use to sign in. Remember it? <Link to="/login" className="text-accent hover:underline">Back to sign in</Link>.
              </div>
            </div>
            <div>
              <Label htmlFor="email" className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm mt-1.5" data-testid="fp-email" autoFocus />
            </div>
            <Button type="submit" disabled={busy}
              className="w-full rounded-sm bg-logo-gradient hover:opacity-95 text-white h-11"
              data-testid="fp-submit">
              <Mail className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Email me a reset link"}
            </Button>
            <div className="text-[10px] text-foreground/50">
              For your security, we won't confirm whether the email exists in our system.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
